/* global document */
import React from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { createRoot } from 'react-dom/client'
import { connect } from 'react-redux'

import { hideBodyScroll, showBodyScroll } from 'js/shared/utils'
import useGmapObject, {mapIDs} from '../../../gmap/GmapObject'
import setActiveHotel from '../../actions/setActiveHotel'
import setValue from '../../../shared/actions/setValue'
import showMobileMap from '../../actions/showMobileMap'
import {pushMarkerMap, pushFullMap, pushFullMapGA4, mapInteractGA4} from '@helpers/gtm'
import ZoomButton from './ZoomButton'
import ZoomButtonOut from './ZoomButtonOut';


import './googlemap.scss'

const mapZoomTextIn = window?.drupalSettings?.widgets?.mapZoom?.textZoomIn ?? '' 
const mapZoomTextOut = window?.drupalSettings?.widgets?.mapZoom?.textZoomOut ?? '' 

class GoogleMap extends React.Component {
  markers = []

  isMobile = false

  static propTypes = {
    setActHotel: PropTypes.func.isRequired,
    setOwnValue: PropTypes.func.isRequired,
    results: PropTypes.arrayOf(Object),
    style: PropTypes.objectOf(PropTypes.string),
    className: PropTypes.string,
    fullMap: PropTypes.bool,
    isFullMap: PropTypes.bool,
    isOffers: PropTypes.bool.isRequired,
    onShowMobileMap: PropTypes.func.isRequired,
    selectedSliderItem: PropTypes.number,
    zoomButton: PropTypes.elementType,
    zoomOutButton: PropTypes.elementType,
  }

  static defaultProps = {
    style: {},
    results: null,
    className: '',
    fullMap: false,
    isFullMap: false,
    selectedSliderItem: null,
    zoomButton: ZoomButton,
    zoomOutButton: ZoomButtonOut,
  }

  constructor(props) {
    super(props)
  }

  componentDidMount() {
    useGmapObject().then((google) => {
      this.google = google
      this.bounds = new google.maps.LatLngBounds()
      this.map = new this.google.maps.Map(this.mapContainer, {
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        mapId: mapIDs.default_interactive,
        disableDefaultUI: true,
        zoomControl: false, // We use custom zoom buttons.
        maxZoom: 16,
        zoom: 8,
      })
      const itemData = this.props?.results?.find(el => el.id === this.props.selectedSliderItem)
      if (itemData && !itemData?.active) {
        this.setActiveHotel(itemData)
      }

      this.google.maps.event.addListenerOnce(this.map, 'idle', this.onMapInitialised);

      this.map.controls[google.maps.ControlPosition.TOP_RIGHT].push(this.renderZoomBtn())
      this.map.controls[google.maps.ControlPosition.TOP_LEFT].push(this.renderZoomControlBtns())
      this.initMarkers(this.props)
      this.map.fitBounds(this.bounds)
    })
  }

  componentWillReceiveProps(nextProps) {
    const { selectedSliderItem, results } = this.props
    if (nextProps?.results?.length && JSON.stringify(nextProps.results) !== JSON.stringify(results)) {
      this.initMarkers(nextProps)
    }
    if (nextProps.selectedSliderItem !== selectedSliderItem) {
      const itemData = nextProps?.results?.find(el => el.id === nextProps.selectedSliderItem)
      if (!itemData.active && this.map) {
        this.setActiveHotel(itemData)
      }
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const {
      isOffers, results, fullMap,
    } = this.props
    return [
      this.state !== nextState,
      nextProps.isOffers !== isOffers,
      JSON.stringify(nextProps.results) !== JSON.stringify(results),
      nextProps.fullMap !== fullMap,
    ].some(item => item)
  }

  onMapInitialised = () => {
    document.body.classList.add('map-initialised')
  }

  setActiveHotel = (hotel) => {
    const { setActHotel } = this.props
    setActHotel(hotel)
    pushMarkerMap()
    mapInteractGA4('hotel bloc')

    const event = new CustomEvent('mapPointActive', {
      detail: { hotelId: hotel?.id, isSameHotel: hotel?.active }
    });
    this.map.setCenter(hotel.coordinates);
    document.dispatchEvent(event);
  }

  getMarkerImage = (text, color, isActive = false) => {
    const priceTag = document.createElement('div');
    priceTag.className = `gmap-marker ${color} ${isActive ? 'active' : ''}`;
    priceTag.textContent = text;

    return priceTag
  }

  initMarkers = (props) => {
    if (!this.google) {
      return
    }

    if (this.markers.length > 0) {
      this.markers.forEach(marker => marker.setMap(null))
    }

    this.markers = props.results.map((hotel, index) => {
      let hotelPrice;

      if (!hotel?.price) {
          hotelPrice = ' ';
      } else if (hotel?.price?.length > 0) {
        const price = [...hotel?.price?.match(/(\d+|[a-z]+)/g)]
        const currency = [...hotel?.price?.match(/[^0-9\.]+/g)]

        hotelPrice = currency[0] !== '€' ?
          `${currency}${Math.ceil(Number(price.join('.')))}`
        :
          `${Math.ceil(Number(price.join('.')))}${currency}`
      }

      const iconColor = props.isOffers ? 'orange' : 'red'
      const markerPin = this.getMarkerImage(hotelPrice, iconColor, hotel.active);

      const marker = new this.google.maps.marker.AdvancedMarkerElement({
        id: hotel.id,
        position: hotel.coordinates,
        map: this.map,
        gmpClickable: true,
        zIndex: hotel.active ? 9999 : (hotelPrice?.trim()?.length ? 9000 + index : 8000 + index),
        content: markerPin,
        title: hotel.title,
      })

      marker.addListener('gmp-click', () => this.setActiveHotel(hotel))

      this.bounds.extend(
        new this.google.maps.LatLng(marker.position.lat, marker.position.lng)
      )

      if (hotel.active) {
        this.activeHotel = marker
      }

      return marker
    })
    const activeHotel = props.results.find(hotel => hotel.active);
    if (activeHotel) {
      this.map.setCenter(activeHotel.coordinates)
    }
  }

  handleZoomBtnClick = () => {
    const { fullMap, setOwnValue, onShowMobileMap } = this.props
    if (fullMap) {
      onShowMobileMap(this.isMobile)
      showBodyScroll()
      document.body.classList.remove('full-header', 'no-scroll-full-map')
    } else {
      hideBodyScroll()
      document.body.classList.add('full-header', 'no-scroll-full-map')
    }
    setOwnValue('fullMap', !fullMap)
    pushFullMap()
    pushFullMapGA4()
  }

  renderZoomBtn = () => {
    const { isFullMap, zoomButton, zoomOutButton } = this.props
    const Zoom = isFullMap ? zoomOutButton : zoomButton;
    const position = isFullMap
      ? {top: 32, right: 32}
      : {top: 16, right: 16}
    const zoomBtn = document.createElement('div')
    const root = createRoot(zoomBtn);
    root.render(<Zoom
      style={{ marginTop: position.top, marginRight: position.right }}
      onClick={this.handleZoomBtnClick}
    />)

    return zoomBtn
  }

  renderZoomControlBtns = () => {
    const { isFullMap } = this.props;
    const map = this.map;
    const position = isFullMap ? {top: 32, left: 32} : {top: 16, left: 16}
    const zoomBtn = document.createElement('div')
    const root = createRoot(zoomBtn);

    const onZoomClick = function (e, action) {
      e.preventDefault();
      e.stopPropagation();

      const zoom = map.getZoom();
      map.setZoom(action ? zoom + 1 : zoom - 1);
    }

    root.render(
      <div className="btn-zoom-wrap" style={{ marginTop: position.top, marginLeft: position.left }}>
        <button className="btn-zoom" type="button" onClick={(e) => onZoomClick(e, true)}>
          <span aria-hidden="true">+</span>
          <span className="sr-only">{mapZoomTextIn}</span>
        </button>
        <button className="btn-zoom btn-zoom--out" type="button" onClick={(e) => onZoomClick(e, false)}>
          <span aria-hidden="true">–</span>
          <span className="sr-only">{mapZoomTextOut}</span>
        </button>
      </div>
    )

    return zoomBtn
  }

  render() {
    const { style, className, hidden } = this.props

    return (
      <div
        ref={(mapContainer) => { this.mapContainer = mapContainer }}
        className={cn('google-map', className)}
        style={{...style, display: hidden ? 'none' : 'block'}}
      />
    )
  }
}


export default connect(
  ({
    main: {
      fullMap,
    },
    form,
  }) => ({
    fullMap,
    form,
  }),
  dispatch => ({
    setOwnValue: (key, value) => dispatch(setValue(key, value)),
    onShowMobileMap: isMobile => dispatch(showMobileMap(isMobile)),
    setActHotel: hotel => dispatch(setActiveHotel(hotel)),
  }),
)(GoogleMap)
